// Turn links: a one-time link, minted per announcement, that the player
// clicks in Roll20 chat to end their turn. The worker keeps the link and its
// click (/api/turn-links); the Initiative panel polls and steps the tracker.
import { ApiError, api } from "../api";
import { cleanRoll20Name } from "./text";

export interface TurnLink { token: string; url: string; entryId: string; entryName: string; round: number }
interface Current { token: string; entryId: string; entryName: string; round: number; createdAt: string; passedAt: string | null }

// A minted link is only usable if the worker actually returned a token AND a url. The
// response type declares `ok`, so a 200 {ok:false} (or any body missing token/url) is an
// expected shape — reading r.token/r.url blindly yields undefined and posts "[End turn](undefined)"
// into Roll20 chat, a dead link the caller's try/catch never sees (no throw on a 200). Throw here
// so the caller omits the link instead of announcing a broken one.
function mintedOrThrow(r: { ok?: boolean; token?: string; url?: string } | null): { token: string; url: string } {
  if (!r || r.ok === false || typeof r.token !== "string" || !r.token || typeof r.url !== "string" || !r.url)
    throw new ApiError("The turn-link service did not return a usable link.", 200, r);
  return { token: r.token, url: r.url };
}

export async function mintTurnLink(entryId: string, entryName: string, round: number): Promise<TurnLink> {
  const r = await api<{ ok: boolean; token: string; url: string }>("/api/turn-links", { method: "POST", body: JSON.stringify({ entryId, entryName, round }) });
  return { ...mintedOrThrow(r), entryId, entryName, round };
}
export async function pollTurnLink(): Promise<Current | null> {
  const r = await api<{ ok: boolean; link: Current | null }>("/api/turn-links/current");
  return r.link;
}

/** The same lane again, for "you moved into its space — onto, under or
 *  through?". The token keys the three chat buttons, so an answer to an
 *  older question cannot resolve this one. */
export async function mintMoveLink(key: string, label: string): Promise<TurnLink> {
  const r = await api<{ ok: boolean; token: string; url: string }>("/api/turn-links", { method: "POST", body: JSON.stringify({ entryId: `move:${key}`, entryName: label, round: 1 }) });
  return { ...mintedOrThrow(r), entryId: `move:${key}`, entryName: label, round: 1 };
}

/** The same one-time-link lane, for confirming where a spell template landed. The caster
 *  drags the marker and clicks the link; the engine rolls saves for whatever is inside it
 *  at that moment. Kept apart from the turn lane by the entry id, so an armed spell and a
 *  live turn link never hide each other. */
export async function mintPlaceLink(effectId: string, label: string): Promise<TurnLink> {
  const r = await api<{ ok: boolean; token: string; url: string }>("/api/turn-links", { method: "POST", body: JSON.stringify({ entryId: `place:${effectId}`, entryName: label, round: 1 }) });
  return { ...mintedOrThrow(r), entryId: `place:${effectId}`, entryName: label, round: 1 };
}
export async function pollPlaceLink(): Promise<Current | null> {
  const r = await api<{ ok: boolean; link: Current | null }>("/api/turn-links/current?kind=place");
  return r.link;
}
/** One link by its own token: several spells can be waiting to be placed at once, and
 *  "the newest link" would let the second caster's mint hide the first caster's answer. */
export async function pollLinkToken(token: string): Promise<Current | null> {
  const r = await api<{ ok: boolean; link: Current | null }>(`/api/turn-links/${encodeURIComponent(token)}`);
  return r.link;
}

// ---- how a player is offered the click ----
// A web link in Roll20 chat raises Roll20's "you are about to leave this site"
// dialog, and players simply did not click through it. A chat BUTTON posts
// text as the player's own chat line instead, which the connector (0.8.185)
// reads back: no dialog, no page, nothing to install.
//
// The syntax matters more than it looks. "[End turn](!dms end <token>)" is an
// API command button, and Roll20's own documentation is contradictory about
// what one does in a game with no Mod running (this table has none). A
// BACKTICK at the start of the command is the documented text-only form: the
// button stops being a command to execute and simply outputs its text, on any
// account tier. That is all this needs — the DM Screen is listening to chat,
// not to Roll20's script sandbox — so the backtick form is the one used.
// The connector's parser looks for !dms anywhere in the line, so the leading
// backtick (shown or swallowed, depending on Roll20) changes nothing.
//
// "both" offers the button and the link, which is the honest default until a
// table has seen which one its players actually use.
/** The word a player types to end their own turn; the connector reads who said it. */
export const TYPED_END = "done";
/* "link" is a labelled web link, which is the only clickable form that works.
   The value is still called "button" in saved layouts so nobody's setting is
   lost; see chatAction for why there is no button any more. */
export type LinkStyle = "button" | "url" | "both";
export const linkStyleOf = (layout: Record<string, unknown>, _canCommands: boolean): LinkStyle => {
  const s = String(layout.playerLinkStyle || "");
  if (s === "button" || s === "url" || s === "both") return s;
  return "button";   // one short labelled link, and the typed instruction
};

/**
 * The clickable part of a chat line.
 *
 * THERE IS NO CHAT BUTTON. This used to emit Roll20's `[Label](!command)` form,
 * which turns a click into a chat message a script can answer — except that the
 * turning is done by the Mod/API layer, and without a Mod script Roll20 renders
 * it as a bare `<a href="!dms end …">`: a relative URL that goes nowhere.
 * Measured in the DM's own game on 2026-09-27, both with and without the
 * backtick; neither produced a chat message, and both rendered as plain anchors
 * with no handler attached. It had been in every turn announcement for weeks,
 * teaching players that the first link is broken.
 *
 * So the clickable form is a real web link, written `[End turn](url)` so the
 * line carries a short label instead of a hundred characters of token. Roll20
 * renders that as a working anchor showing only the label — verified the same
 * way. It still raises Roll20's leave-the-site prompt, which is inherent to
 * leaving Roll20 and is why the typed word remains the instruction.
 *
 * `done` is the thing that cannot fail: no button, no link, no dialog, no Mod.
 * Roll20 already says who spoke and the tracker already knows whose turn it is.
 */
/** The ability the connector puts on each player's character (endturn-ability.js). */
export const END_TURN_ABILITY = "EndTurn";

export function chatAction(label: string, verb: string, link: TurnLink, style: LinkStyle, speakAs?: string): string {
  /* A character ABILITY when we know whose character it is, because that is the
     one clickable form that works: Roll20 runs abilities itself, no Mod script,
     no leaving Roll20 and so no leave-the-site prompt. The ability holds
     "/me has finished their turn." and the emote is read back by roll20.js.
     Everything else falls back to the web link. */
  /* The character name is what Roll20 resolves the moment the line is posted, so
     an invisible stray character in it (a zero-width space pasted into a sheet
     name) reds the players' chat with "Unable to find a player or character".
     Clean it to the name Roll20 actually knows; an empty result falls back to
     the web link rather than emitting `[End turn](~|EndTurn)`. */
  const named = speakAs ? cleanRoll20Name(speakAs) : "";
  const ability = named ? `[${label}](~${named}|${END_TURN_ABILITY})` : "";
  const linked = `[${label}](${link.url})`;
  const typed = verb === "end" ? ` — or type ${TYPED_END} in chat` : "";
  if (style === "url") return ` — ${label}: ${link.url}${typed}`;
  if (style === "both") return ` ${ability || linked} — or ${link.url}${typed}`;
  return ` ${ability || linked}${typed}`;
}
